import { inject } from '@angular/core';
import { LOGGER_FACTORY } from '../core/logger/logger-factory.provider.js';
import { HttpInterceptorFn } from '@angular/common/http';
import { Auth, getIdToken } from '@angular/fire/auth';
import { from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AnalyticsTrackingService } from '../core/analytics-tracking.service.js';
import { AppsflyerWebService } from '../core/appsflyer-web.service';

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const auth = inject(Auth, { optional: true })!;
  const analyticsTracking = inject(AnalyticsTrackingService);
  const appsflyerWeb = inject(AppsflyerWebService);
  const logger = inject(LOGGER_FACTORY).create('AuthInterceptor');

  if (req.url.startsWith('https://cdn')) {
    return next(req);
  }

  // Wait for Firebase Auth to be ready before checking for auth token.
  // This ensures we always use the most up-to-date token, especially after
  // operations like KYC completion that call getIdToken(true) to refresh custom claims.
  // Note: On SSR, currentUser is null (expected). Requests proceed without auth header.
  return from(auth.authStateReady()).pipe(
    switchMap(() => {
      const user = auth.currentUser;
      if (!user) {
        return of(null);
      }
      return from(getIdToken(user));
    }),
    catchError((error) => {
      logger.error('Failed to get auth token', { error });
      return of(null);
    }),
    switchMap((authToken) => {
      const isGoogleApiRequest = req.url.includes('places.googleapis.com');

      const googleApiHeader: Record<string, string> = isGoogleApiRequest
        ? { 'X-Goog-Api-Key': environment.google.placesApiKey }
        : {};
      const authTokenHeader: Record<string, string> =
        !isGoogleApiRequest && authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const contentType: Record<string, string> =
        req.body instanceof FormData
          ? {} // Weirdly enough, the Content-Type needs to be undefined for Angular to correctly set the boundary parameter for multipart requests
          : { 'Content-Type': `application/json` };

      const { utm_params, meta_params, snapchat_params, tiktok_params, moloco_id } =
        analyticsTracking.getParams() ?? {};
      const webAppsflyerId = appsflyerWeb.getWebAppsFlyerId();

      // Sent as custom headers in case the cookie is lost due to cross-site request issues/reverse proxy
      const trackingHeaders = {
        'X-Gains-Platform': 'web',
        ...(utm_params
          ? {
              'X-Gains-UTM': JSON.stringify(utm_params),
            }
          : {}),
        ...(meta_params?.fbclid
          ? {
              'X-Gains-Fbclid': meta_params.fbclid,
            }
          : {}),
        ...(meta_params?.fbc
          ? {
              // Bug 3: forward the formatted `_fbc` cookie value
              // server-side when SSR/proxy paths strip the cookie itself.
              'X-Gains-Fbc': meta_params.fbc,
            }
          : {}),
        ...(meta_params?.fbp
          ? {
              'X-Gains-Fbp': meta_params.fbp,
            }
          : {}),
        ...(snapchat_params?.sc_click_id
          ? {
              'X-Gains-Sccid': snapchat_params.sc_click_id,
            }
          : {}),
        ...(snapchat_params?.sc_cookie1
          ? {
              'X-Gains-Sccookie': snapchat_params.sc_cookie1,
            }
          : {}),
        ...(tiktok_params?.ttclid
          ? {
              'X-Gains-Ttclid': tiktok_params.ttclid,
            }
          : {}),
        ...(tiktok_params?.ttp
          ? {
              'X-Gains-Ttp': tiktok_params.ttp,
            }
          : {}),
        ...(moloco_id
          ? {
              // Forward the Moloco CTV web click-id server-side when
              // SSR/proxy paths strip the `gains_mlcc` / `_mlcc_v2` cookie.
              'X-Gains-Moloco-Id': moloco_id,
            }
          : {}),
        ...(webAppsflyerId
          ? {
              // AppsFlyer Web SDK visitor ID (`afUserId` cookie),
              // persisted on the signup attribution row for reconciliation
              // with AppsFlyer cross-platform raw data.
              'X-Gains-Af-Web-Id': webAppsflyerId,
            }
          : {}),
      };

      const headers = {
        ...contentType,
        ...googleApiHeader,
        ...authTokenHeader,
        ...(req.url.startsWith(environment.apiUrl) ? trackingHeaders : {}),
      };

      req = req.clone({
        setHeaders: headers,
      });

      return next(req);
    }),
  );
};
